import { FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP } from '@eventbrite/search-utils';
import shuffle from 'lodash/shuffle';
import { createSelector } from 'reselect';
import { DiscoverState } from '../../../../../types/index';
import { topCategories, topDates, topFormats } from '../constants';

const getSuggestions = (state: DiscoverState) => state.suggestions;
const getEventSearch = (state: DiscoverState) => state.search.eventSearch;

const getSuggestedFilters = createSelector(
    [getSuggestions],
    (suggestions) => suggestions.filters || [],
);

const getSuggestedTags = createSelector(
    [getSuggestions],
    (suggestions) => suggestions.tags || [],
);

const getFilterDisplayValue = (filter: { type: string; value: string }) => {
    const filterType = FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[filter.type];

    const filterValue = filterType.values.find(
        ({ value }: { value: string }) => value === filter.value,
    );

    return filterValue?.display;
};

const _formatFiltersForRender = (filters: { type: string; value: string }[]) =>
    shuffle(
        filters
            .filter((filter) => getFilterDisplayValue(filter))
            .map((filter) => {
                const display = getFilterDisplayValue(filter);
                return {
                    ...filter,
                    display,
                };
            }),
    );

/**
 * Parse returned filters into format suitable for the
 * consuming component.
 *
 * Obey the following rules:
 *  1. Prioritize filters present in topCategories/topFormats/topPrice
 *  2. If total list <10 after prioritization, include top remaining filters
 *  3. If there are >=3 filters, include the free filter
 *  4. Shuffle returned array
 */
export const getParsedSuggestedFilters: any = createSelector(
    [getSuggestedFilters, getEventSearch],
    (filters, eventSearch) => {
        if (filters.length < 4) {
            return [];
        }

        //We don't want to suggest a filter that is already applied.
        const filtersNotCurrentlyInUse = filters.filter(
            (filter: { value: string }) => {
                //check eventSearch dates
                if (eventSearch.dates === filter.value) {
                    return false;
                }

                if (eventSearch?.tags?.includes(filter.value)) {
                    return false;
                }

                return true;
            },
        );

        //Priortize filters defined in our top 10 lists
        let prioritizedFilterValues = [
            ...filtersNotCurrentlyInUse.filter(({ value }: { value: string }) =>
                [...topCategories, ...topFormats, ...topDates].includes(value),
            ),
        ];

        //append difference to end of array
        prioritizedFilterValues = [
            ...prioritizedFilterValues,
            ...filtersNotCurrentlyInUse.reduce(
                (memo: any, filter: { value: string }) => {
                    const filterInPrioritized = prioritizedFilterValues.find(
                        ({ value }) => filter.value === value,
                    );
                    if (filterInPrioritized) {
                        return memo;
                    } else {
                        return [...memo, filter];
                    }
                },
                [],
            ),
        ];

        return _formatFiltersForRender([
            ...prioritizedFilterValues.slice(0, 10),
            { type: 'price', value: 'free' },
        ]);
    },
);

export const getRandomizedSuggestedTags: any = createSelector(
    [getSuggestedTags],
    (tags) => {
        const shuffledTags = shuffle(tags);
        return shuffledTags.slice(0, 20);
    },
);
